import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/kit";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import { Txt } from "@/components/Txt";

const box = "w-full bg-warm-cream border border-ink/15 rounded-lg px-3 py-2 text-[14px]";
const ORDER = ["payment_signup", "recurring_charge", "lapse_14", "lapse_3", "payment_failed",
  "event_reminder", "checkin_points", "fulfillment_mailed", "targeted_ask", "account_suspended",
  "visitor_first_interaction", "renewal_nudge", "grant_deadline"];
const SIMULATED = new Set(["payment_signup", "recurring_charge", "payment_failed"]);

export default function NotificationSettings({ me }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newsletterUrl, setNewsletterUrl] = useState("");
  const [nlSaving, setNlSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const load = useCallback(async () => { const r = await api.get("/notifications-config"); setCfg(r.data.notifications); }, []);
  useEffect(() => { load(); }, [load]);
  const isSuper = me?.user?.is_super_admin;
  const [tileCfg, setTileCfg] = useState(null);
  const [tileCat, setTileCat] = useState([]);
  const [roles, setRoles] = useState([]);
  useEffect(() => {
    if (!isSuper) return;
    api.get("/dashboard/tiles").then((r) => { setTileCat(r.data.catalog || []); setTileCfg(r.data.config || {}); }).catch(() => {});
    api.get("/roles").then((r) => setRoles((r.data.roles || r.data || []).filter((x) => (x.layout || "app") === "admin" || x.role_id === "super_admin"))).catch(() => {});
  }, [isSuper]);
  const toggleTile = (roleId, tileId) => {
    const cur = tileCfg[roleId] || tileCat.map((t) => t.id);
    const next = cur.includes(tileId) ? cur.filter((x) => x !== tileId) : [...cur, tileId];
    setTileCfg({ ...tileCfg, [roleId]: next });
  };
  const saveTiles = async () => {
    try { await api.put("/dashboard/tiles", { config: tileCfg }); toast.success("Dashboard tiles saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  useEffect(() => { api.get("/newsletter-link").then((r) => setNewsletterUrl(r.data.url || "")).catch(() => {}); }, []);

  const saveNewsletter = async () => {
    setNlSaving(true);
    try { await api.put("/newsletter-link", { url: newsletterUrl.trim() }); toast.success("Newsletter link saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setNlSaving(false);
  };
  const syncAll = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/newsletter-sync-all");
      const { synced, total, failed } = r.data;
      toast.success(`Synced ${synced} of ${total} members to Mailchimp${failed ? ` (${failed} could not be added)` : ""}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not sync"); }
    setSyncing(false);
  };

  const canDonations = isSuper || ["edit", "full"].includes(me?.permissions?.donations);
  const [ds, setDs] = useState(null);
  const [dsSaving, setDsSaving] = useState(false);
  useEffect(() => { if (canDonations) api.get("/donation-settings").then((r) => setDs(r.data)).catch(() => {}); }, [canDonations]);
  const saveDs = async () => {
    setDsSaving(true);
    try { await api.put("/donation-settings", ds); toast.success("Donation receipt settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setDsSaving(false);
  };

  const upd = (k, field, v) => setCfg({ ...cfg, [k]: { ...cfg[k], [field]: v } });
  const save = async () => {
    setSaving(true);
    try { await api.put("/notifications-config", { notifications: cfg }); toast.success("Notification settings saved"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
    setSaving(false);
  };

  if (!cfg) return <div className="text-ink/60 py-8">Loading…</div>;
  return (
    <div data-testid="notification-settings-page">
      <h1 className="font-zilla text-3xl sm:text-4xl font-semibold mb-1"><Txt id="notif.title" screen="Notifications">Notifications</Txt></h1>
      <p className="text-[16px] text-ink/70 mb-5"><Txt id="notif.subtitle" screen="Notifications">These emails send automatically. Switch any of them on or off and edit the wording. Use {"{name}"}, {"{amount}"}, {"{date}"}, {"{event}"}, {"{balance}"}, {"{reward}"}, {"{link}"} as placeholders.</Txt></p>

      <div className="bg-cream border border-ink/15 rounded-xl p-4 max-w-3xl mb-6" data-testid="newsletter-link-setting">
        <div className="flex items-center gap-2 mb-1"><Icon name="MessageSquare" className="w-4 h-4 text-maroon" /><span className="text-[15px] font-semibold">Latest newsletter link</span></div>
        <p className="text-[13px] text-ink/60 mb-2">Paste the Mailchimp campaign "view in browser" link. Members see a small "See the latest newsletter" button on their dashboard.</p>
        <div className="flex gap-2">
          <input data-testid="newsletter-url-input" value={newsletterUrl} onChange={(e) => setNewsletterUrl(e.target.value)} placeholder="https://mailchi.mp/..." className={box} />
          <Button data-testid="newsletter-save" onClick={saveNewsletter} disabled={nlSaving} className="px-4 min-h-[42px] text-[14px] shrink-0">{nlSaving ? "…" : "Save"}</Button>
        </div>
        <div className="mt-3 pt-3 border-t border-ink/10 flex items-center justify-between gap-3 flex-wrap">
          <span className="text-[13px] text-ink/60">Add every current member's email to your Mailchimp audience.</span>
          <Button variant="secondary" data-testid="newsletter-sync-all" onClick={syncAll} disabled={syncing} className="px-4 min-h-[40px] text-[14px] shrink-0">{syncing && <Icon name="RefreshCw" className="w-4 h-4 animate-spin" />}{syncing ? "Syncing…" : "Sync all existing members"}</Button>
        </div>
      </div>

      {canDonations && ds && (
        <div className="bg-cream border border-ink/15 rounded-xl p-4 max-w-3xl mb-6" data-testid="donation-receipt-settings">
          <div className="flex items-center gap-2 mb-1"><Icon name="HeartHandshake" className="w-4 h-4 text-maroon" /><span className="text-[15px] font-semibold">Donation receipts</span></div>
          <p className="text-[13px] text-ink/60 mb-3">Used in the donation thank-you email below. Gifts of $250+ must state in writing whether goods or services were provided. Use {"{ein}"} in the tax statement — it fills in from the EIN field.</p>
          <div className="grid sm:grid-cols-2 gap-3 mb-2">
            <div><label className="text-[12px] font-semibold text-ink/60 block mb-1">Organization EIN</label><input data-testid="ds-ein" value={ds.ein} onChange={(e) => setDs({ ...ds, ein: e.target.value })} placeholder="e.g. 36-1234567" className={box} /></div>
            <div><label className="text-[12px] font-semibold text-ink/60 block mb-1">Organization name</label><input data-testid="ds-org" value={ds.org_name} onChange={(e) => setDs({ ...ds, org_name: e.target.value })} className={box} /></div>
          </div>
          <label className="text-[12px] font-semibold text-ink/60 block mb-1">Tax statement</label>
          <textarea data-testid="ds-tax" rows={3} value={ds.tax_statement} onChange={(e) => setDs({ ...ds, tax_statement: e.target.value })} className={box} />
          <Button data-testid="ds-save" onClick={saveDs} disabled={dsSaving} className="mt-3 px-4 min-h-[42px] text-[14px]">{dsSaving ? "Saving…" : "Save receipt settings"}</Button>
        </div>
      )}

        <div className="bg-cream border border-ink/15 rounded-xl p-4 max-w-3xl mb-6" data-testid="tile-config">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[15px] font-semibold">Admin dashboard tiles</span>
            <Button data-testid="tile-config-save" onClick={saveTiles} className="px-4 min-h-[38px] text-[13px]">Save</Button>
          </div>
          <p className="text-[13px] text-ink/60 mb-3">Choose which stat tiles each admin role sees on their dashboard.</p>
          <div className="space-y-3">
            {roles.map((role) => {
              const on = tileCfg[role.role_id] || tileCat.map((t) => t.id);
              return (
                <div key={role.role_id} data-testid={`tile-role-${role.role_id}`}>
                  <div className="text-[13px] font-semibold mb-1">{role.name || role.role_id}</div>
                  <div className="flex flex-wrap gap-1.5">
                    {tileCat.map((t) => (
                      <button key={t.id} data-testid={`tile-opt-${role.role_id}-${t.id}`} onClick={() => toggleTile(role.role_id, t.id)}
                        className={`text-[12px] px-2.5 py-1 rounded-full border ${on.includes(t.id) ? "bg-ink text-cream border-ink" : "border-ink/25 text-ink/60"}`}>{t.label}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="space-y-3 max-w-3xl">
        {[...ORDER, ...Object.keys(cfg).filter((k) => !ORDER.includes(k))].map((k) => {
          const n = cfg[k]; if (!n) return null;
          return (
            <div key={k} data-testid={`notif-${k}`} className={`bg-cream border border-ink/15 rounded-xl p-4 ${n.enabled ? "" : "opacity-70"}`}>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold">{n.label}</span>
                  {SIMULATED.has(k) && <span className="text-[11px] px-2 py-0.5 rounded-full bg-gold/25 text-ink font-semibold">SIMULATED (Square)</span>}
                  {n.channel === "in_app" && <span className="text-[11px] px-2 py-0.5 rounded-full bg-ink/10 font-semibold">in-app alert</span>}
                </div>
                <label className="inline-flex items-center gap-2 text-[13px] font-semibold shrink-0">
                  <input type="checkbox" data-testid={`notif-toggle-${k}`} checked={n.enabled} onChange={(e) => upd(k, "enabled", e.target.checked)} />
                  {n.enabled ? "On" : "Off"}
                </label>
              </div>
              <label className="text-[12px] font-semibold text-ink/60 block mb-1">Subject</label>
              <input data-testid={`notif-subject-${k}`} value={n.subject} onChange={(e) => upd(k, "subject", e.target.value)} className={box + " mb-2"} />
              <label className="text-[12px] font-semibold text-ink/60 block mb-1">{n.channel === "in_app" ? "Alert text" : "Body (HTML allowed)"}</label>
              <textarea data-testid={`notif-body-${k}`} rows={3} value={n.body} onChange={(e) => upd(k, "body", e.target.value)} className={box} />
            </div>
          );
        })}
      </div>

      <Button data-testid="notif-save" onClick={save} disabled={saving} className="mt-5 px-5 py-2.5 min-h-[44px]">{saving ? "Saving…" : "Save notification settings"}</Button>
    </div>
  );
}
